import { navigateTo } from '#app' // Nuxt 3 正确的导航方式
import type { ApiResponse, AppsConfig, DownloadApp } from '~/types'
import { Lunar } from 'lunar-javascript';
import { useI18n } from 'vue-i18n'
import type { NuxtApp } from '#app';

//获取日期特殊天
export const getDay = (time: string): string => {
  if (!time || !/^\d{4}-\d{2}-\d{2}$/.test(time)) {
    return ''
  }
  const day = time.split('-')[2]
  // 去除前置0（如27保持，05变为5）
  return day.startsWith('0') ? day.slice(1) : day
}
//获取日期特殊年月
export const getMonthYear = (time: string): string => {
  if (!time || !/^\d{4}-\d{2}-\d{2}$/.test(time)) {
    return ''
  }
  const [year, month] = time.split('-')
  return `${year}/${month}`
}
//获取日期特殊年月日
export const getMonthYearDay = (time: string): string => {
  if (!time || !/^\d{4}-\d{2}-\d{2}$/.test(time)) {
    return ''
  }
  const [year, month, day] = time.split('-')
  return `${year}/${month}/${day}`
}
//获取IP
export const setIp = (headers: any) => {
  const ip = (headers && (headers['x-forwarded-for']?.split(',')[0].trim() || headers['x-real-ip'])) || ''
  console.log(ip)
  return ip
}
//获取日期 月/日
export const formatDateToChineseMd = (time: Date | string): string => {
  // 1. 统一转换为 Date 对象（处理不同入参）
  let date: Date
  if (time instanceof Date) {
    date = time
  } else {
    // 处理字符串：先截取日期部分（去掉时分秒），再转 Date
    const datePart = time.split(' ')[0]
    // 校验基础格式（YYYY-MM-DD）
    if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      return ''
    }
    date = new Date(datePart)
    // 处理无效日期（如 2025-13-01 转 Date 会是 Invalid Date）
    if (isNaN(date.getTime())) {
      return ''
    }
  }

  // 2. 提取月份和日期（自动去除前置0）
  const month = date.getMonth() + 1 // 月份从0开始，需+1
  const day = date.getDate()

  // 3. 拼接为「X月X日」格式
  return `${month}月${day}日`
}

//获取日期 月/日 增加英文判断
// lang = 'zh-CN' | 'en-US';
export const formatDate = (time: Date | string, lang: 'zh-CN' | 'en-US' = 'zh-CN'): string => {
  // 1. 统一转换为 Date 对象（处理不同入参）
  let date: Date
  if (time instanceof Date) {
    date = time
  } else {
    // 处理字符串：先截取日期部分（去掉时分秒），再转 Date
    const datePart = time.split(' ')[0]
    // 校验基础格式（YYYY-MM-DD）
    if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      return ''
    }
    date = new Date(datePart)
    // 处理无效日期（如 2025-13-01 转 Date 会是 Invalid Date）
    if (isNaN(date.getTime())) {
      return ''
    }
  }

  // 2. 提取月份和日期（自动去除前置0）
  const month = date.getMonth()
  const day = date.getDate()

  // 3. 根据语言返回不同格式
  if (lang === 'en-US') {
    // 英文月份名称
    const monthNames = [ 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    // 获取序数后缀
    const getDaySuffix = (day: number): string => {
      if (day > 3 && day < 21) return 'th'; // 11th, 12th, 13th 等
      switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
    return `${monthNames[month]} ${day}${getDaySuffix(day)}`;
  } else {
    // 中文格式
    return `${month + 1}月${day}日`;
  }
}
/**
 * 处理请求错误的返回结果类型
 */
export interface HandleErrorResult {
  isHandled: boolean // 是否处理了错误
  status?: number // 错误状态码，无错误时为undefined
}

/**
 * API 响应处理结果类型
 */
export interface ApiResponseResult<T = any> {
  success: boolean // 请求是否成功
  data?: T // 业务数据
  errorMsg?: string // 错误信息
  status: number // 状态码（0: 成功, 1: 无数据, 2: 错误）
}

export const handleRequestError = (res?: any, error?: any): HandleErrorResult => {
  console.log(res)
  // 1. 处理业务码404
  if (res?.code === 404) {
    navigateTo('/error')
    return { isHandled: true, status: 404 }
  }
  console.log(error)
  // 2. 处理HTTP状态码404（catch中的错误）
  if (error?.status === 404 || error?.response?.status === 404) {
    //navigateTo('/error')
    return { isHandled: true, status: 404 }
  }

  // 3. 处理其他错误（非0业务码/网络错误等）
  if (res?.code !== 0 || error) {
    return { isHandled: true, status: 2 }
  }

  // 无错误
  return { isHandled: false }
}

/**
 * 处理API响应数据
 * @param response - useServerRequest 返回的 data 对象
 * @param emptyMsg - 无数据时的提示信息
 * @returns 处理结果
 */
export const handleApiResponse = <T = any>(
  response: ApiResponse<T> | null | undefined
  //emptyMsg: string = '暂无数据'
): ApiResponseResult<T> => {
  // 1. 检查响应是否存在
  if (!response) {
    return {
      success: false,
      //errorMsg: '请求失败',
      status: 2
    }
  }
  if (response?.code === 404) {
    navigateTo('/error')
    return { success: false, status: 404 }
  }
  // 2. 检查业务码
  if (response.code !== 0) {
    return {
      success: false,
      //errorMsg: response.msg || '请求失败',
      status: 2
    }
  }

  // 3. 检查数据是否存在
  if (!response.data) {
    return {
      success: false,
      //errorMsg: emptyMsg,
      status: 1
    }
  }

  // 4. 成功返回数据
  return {
    success: true,
    data: response.data,
    status: 0
  }
}

// 判断是否为图片/视频
export const isImage = (url: string): boolean => {
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(url)
}
export const isVideo = (url: string): boolean => {
  return /\.(mp4|avi|mov|mkv|rmvb|flv|wmv|webm)$/i.test(url)
}
export const getMediaType = (url: string): 'image' | 'video' | 'other' => {
  if (isImage(url)) return 'image'
  if (isVideo(url)) return 'video'
  return 'other'
}

// 判断设备信息
export const getDevice = () => {
  const ua = navigator.userAgent || ''
  const lowerUA = ua.toLowerCase()
  // Android
  const isAndroid = /android/i.test(ua)
  // Android TV (包含 GoogleTV / SmartTV / TV 关键词)
  const isAndroidTV = /android tv|smart[-\s]?tv|googletv|bravia|aftt|aftb|afts/i.test(ua)
  // iPhone
  const isIPhone = /iphone/i.test(ua)
  // iPad
  const isIPad = /ipad/i.test(ua) || (/(macintosh)/i.test(ua) && 'ontouchend' in document)
  // HarmonyOS（鸿蒙系统，UA 通常包含 HarmonyOS / HMOS）
  const isHarmony = /harmonyos|hmos|huawei/i.test(lowerUA) && /android/i.test(ua) === false
   // Symbian（塞班）
  const isSymbian = /symbianos|series60|nokia/i.test(lowerUA)

  if (isAndroidTV) return 'android_tv'
  if (isHarmony) return 'harmony'
  if (isAndroid) return 'android'
  if (isIPhone) return 'ios'
  if (isIPad) return 'ipad'
  if (isSymbian) return 'symbian'

  return 'pc'
}

export const appDownload = (link: string, packageUrl: string, type: string) => {
  const device = getDevice() as String
  console.log(device, type)
  if ((device === 'PC' || device !== type) && packageUrl) {
    // PC或设备和点击不一致，下载packageUrl
    const a = document.createElement('a')
    a.href = packageUrl
    a.download = ''
    a.style.display = 'none'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  } else {
    // 设备和点击一致，打开link
    window.open(link, '_blank')
  }
}
export const getConfigDownloadApp = (types: Array<string>, apps: AppsConfig) => {
  const download = {} as DownloadApp
  apps.forEach((item) => {
    item.forEach((app) => {
      if (types.includes(app.type)) {
        download[app.type] = app
      }
    })
  });
  return download
}


// 判断是日出还是日落
/**
 * 支持任意时间点的日出日落状态判断（包括第二天凌晨）
 * @param sunRise 日出时间字符串，格式："YYYY-MM-DD HH:mm:ss"
 * @param sunDown 日落时间字符串，格式："YYYY-MM-DD HH:mm:ss"
 * @returns 时间状态："日出前" | "日出后" | "日落后"
 */
export const getSunStatusAnyTime = (sunRise: string, sunDown: string): string => {
  if (!sunRise || !sunDown) {
    return '1'
  }
  // 1. 提取日出日落的时分秒（忽略日期）
  const riseTime = sunRise.split(' ')[1] // "HH:mm:ss"
  const downTime = sunDown.split(' ')[1] // "HH:mm:ss"

  // 2. 获取当前时间的时分秒
  const now = new Date()
  const nowTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now
    .getSeconds()
    .toString()
    .padStart(2, '0')}`

  // 3. 比较逻辑（时间是24小时循环的）
  if (nowTime >= riseTime && nowTime < downTime) {
    // 当前时间在日出后、日落前 → 日出后
    //日落后
    return '2'
  } else if (nowTime >= downTime || nowTime < riseTime) {
    // 当前时间在日落后 或 日出前
    // 进一步判断：如果日落时间 < 日出时间（跨天情况，如极昼极夜），则特殊处理
    if (downTime < riseTime) {
      // 跨天情况（如日落20:00，日出05:00）
      if (nowTime >= riseTime || nowTime < downTime) {
        return '2'
      } else {
        return '2'
      }
    } else {
      // 正常情况（日出 < 日落）
      if (nowTime >= downTime) {
        return '2'
      } else {
        return '1'
      }
    }
  }

  return '未知' // 理论上不会走到这里
}


// 根据 AQI 数值获取空气质量信息


/**
 * 获取 AQI 配置（延迟加载翻译文案，支持语言切换）
 * @param nuxtApp Nuxt 实例（可选，用于服务端渲染）
 * @returns AQI 配置数组
 */
function resolveI18nT(nuxtApp?: NuxtApp) {
  const fallback = (key: string) => key
  if (nuxtApp?.$i18n?.t) {
    try {
      return nuxtApp.$i18n.t.bind(nuxtApp.$i18n)
    } catch {
      /* fall through */
    }
  }
  try {
    return useI18n().t
  } catch {
    return fallback
  }
}

export const getAQIInfo = (aqi: number, nuxtApp?: NuxtApp) => {
  const t = resolveI18nT(nuxtApp)
  const safeT = (key: string) => {
    try {
      return t(key)
    } catch {
      return key
    }
  }
  const AQI_CONFIG = [
    { min: 0, max: 50,  text: safeT('aqi.excellent.text'),  level: safeT('aqi.excellent.level'),color: '#52c41a' },
    { min: 51, max: 100, text: safeT('aqi.good.text'),  level: safeT('aqi.good.level'), color: '#f3c85a' },
    { min: 101, max: 150,text: safeT('aqi.lightPollution.text'),  level: safeT('aqi.lightPollution.level'), color: '#ff784a' },
    { min: 151, max: 200, text: safeT('aqi.moderatePollution.text'),  level: safeT('aqi.moderatePollution.level'), color: '#ff4d4f' },
    { min: 201, max: 300, text: safeT('aqi.heavyPollution.text'), level: safeT('aqi.heavyPollution.level'),  color: '#722ed1' },
    { min: 301, max: 500, text: safeT('aqi.severePollution.text'), level: safeT('aqi.severePollution.level'),color: '#531dab' }
  ]
  const config = AQI_CONFIG.find((config) => aqi >= config.min && aqi <= config.max)
  return config || { level: '', color: '', text: '' }
}


//获取农历日期
// export const getLunarDate = (date: Date = new Date()): string => {
//   const lunar = Lunar.fromDate(date)
//   return lunar.getMonthInChinese() + lunar.getDayInChinese()
// }
export const getLunarDate = (date: Date = new Date()): string => {
  try {
    // 1. 创建农历对象（API 稳定，所有版本都支持）
    const lunar = Lunar.fromDate(date);
    
    // 2. 处理闰月（兼容 isLeap() 和 isRunMonth() 两种方法）
    const isLeapMonth = lunar.isLeap?.() || lunar.isRunMonth?.() || false;
    
    // 3. 获取农历月份（兼容不同版本的月份获取方法）
    const monthNum = lunar.getMonth() || 1; // 月份数字（1=正月，12=腊月）
    const monthChars = ['正', '二', '三', '四', '五', '六', '七', '八', '九', '十', '冬', '腊'];
    const lunarMonth = isLeapMonth 
      ? `闰${monthChars[monthNum - 1]}` 
      : monthChars[monthNum - 1];
    
    // 4. 获取农历日期（兼容 getDayInChinese() 和 直接获取日中文）
    const lunarDay = lunar.getDayInChinese?.() || '';
    // 兜底修正日期格式（避免极少数情况的格式错误）
    const fixedDay = lunarDay
      .replace(/^十$/, '初十')
      .replace(/^廿$/, '二十')
      .replace(/^卅$/, '三十');
    
    // 5. 拼接完整格式
    return `${lunarMonth}月${fixedDay}`;
  } catch (error) {
    console.error('获取农历日期失败：', error);
    return '农历日期获取失败';
  }
};

//获取星期
export const getWeekDay = (date: Date = new Date(), nuxtApp?: NuxtApp): string => {
  const t = resolveI18nT(nuxtApp)
  const weekArr = [0, 1, 2, 3, 4, 5, 6].map((i) => {
    try {
      return t(`weekdays.${i}`)
    } catch {
      return String(i)
    }
  })
  return weekArr[date.getDay()] || ''
}


